import { Injectable, inject } from '@angular/core';
import {
  CNHAccountDetails,
  CNHAccountResponse,
  CNHCompany,
  CNHTractor,
  MachineDataApiService,
  MachineDataTask,
  TaskOnlineServices,
} from '@cropx-web/services/api-gateway/MachineDataApiService';
import {
  Action,
  Selector,
  State,
  StateContext,
  createPropertySelectors,
} from '@ngxs/store';
import { produce } from 'immer';
import { Observable, catchError, tap, throwError } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace CNH {
  const type = 'CNH';

  export class GetSignInUrl {
    static readonly type = `[${type}] GetSignInUrl`;

    constructor(
      public companyUuid: string,
      public callbackUrl: string,
    ) {}
  }

  export class GetToken {
    static readonly type = `[${type}] GetToken`;

    constructor(
      public companyUuid: string,
      public code: string,
      public callbackUrl: string,
    ) {}
  }

  export class GetAccount {
    static readonly type = `[${type}] GetAccount`;

    constructor(public companyUuid: string) {}
  }

  export class SetCompany {
    static readonly type = `[${type}] SetCompany`;

    constructor(
      public companyUuid: string,
      public company: CNHCompany,
    ) {}
  }
  export class Disconnect {
    static readonly type = `[${type}] Disconnect`;

    constructor(public companyUuid: string) {}
  }

  export class GetTasks {
    static readonly type = `[${type}] GetTasks`;

    constructor(public companyUuid: string) {}
  }
  export class DownloadTask {
    static readonly type = `[${type}] DownloadTask`;

    constructor(
      public companyUuid: string,
      public jobId: number,
    ) {}
  }
}

export interface CNHStateModel {
  isConnected: boolean | undefined;
  isLoading: boolean;
  signInUrl: string;
  account: CNHAccountResponse;

  tasks: MachineDataTask[];
  tasksLoading: boolean;
}

@State<CNHStateModel>({
  name: 'cnh',
  defaults: {
    isConnected: undefined,
    isLoading: true,
    signInUrl: undefined,
    account: undefined,
    tasks: [],
    tasksLoading: false,
  },
})
@Injectable()
export class CNHState {
  private machineDataApi = inject(MachineDataApiService);

  @Action(CNH.GetSignInUrl)
  getSignInUrl(
    ctx: StateContext<CNHStateModel>,
    action: CNH.GetSignInUrl,
  ): Observable<any> {
    ctx.patchState({ isLoading: true });
    return this.machineDataApi
      .getCNHAuthorizationUrl(action.companyUuid, action.callbackUrl)
      .pipe(
        tap((response: any) => {
          ctx.patchState({
            signInUrl: response.cnh_authorization_url,
          });
        }),
        catchError((err) => {
          ctx.patchState({ isLoading: false });
          return throwError(() => err);
        }),
      );
  }

  @Action(CNH.GetToken)
  getToken(
    ctx: StateContext<CNHStateModel>,
    action: CNH.GetToken,
  ): Observable<any> {
    ctx.patchState({ isLoading: true });
    return this.machineDataApi
      .getCNHCallback(action.companyUuid, action.code, action.callbackUrl)
      .pipe(
        tap(() => {
          const originalUrl =
            window.location.origin + '/partner-connection/user-account/cnh';
          window.history.pushState({ path: originalUrl }, '', originalUrl);

          ctx.dispatch(new CNH.GetAccount(action.companyUuid));
        }),
        catchError((err) => {
          ctx.patchState({ isLoading: false });
          return throwError(() => err);
        }),
      );
  }

  @Action(CNH.Disconnect)
  disconnectConnection(
    ctx: StateContext<CNHStateModel>,
    action: CNH.GetSignInUrl,
  ): Observable<any> {
    ctx.patchState({ isLoading: true });
    return this.machineDataApi.disconnectCNH(action.companyUuid).pipe(
      tap(() => {
        ctx.patchState({
          isConnected: false,
          isLoading: false,
          account: undefined,
        });
      }),
      catchError((err) => {
        ctx.patchState({
          isLoading: false,
          account: undefined,
          isConnected: false,
        });
        return throwError(() => err);
      }),
    );
  }

  @Action(CNH.GetAccount)
  getAccount(
    ctx: StateContext<CNHStateModel>,
    action: CNH.GetAccount,
  ): Observable<any> {
    ctx.patchState({ isLoading: true, account: undefined });
    return this.machineDataApi.getCNHAccount(action.companyUuid).pipe(
      tap((response: CNHAccountResponse) => {
        ctx.patchState({
          isConnected: true,
          isLoading: false,
          account: response,
        });
      }),
      catchError((err) => {
        ctx.patchState({ isLoading: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(CNH.SetCompany)
  setCompany(
    ctx: StateContext<CNHStateModel>,
    action: CNH.SetCompany,
  ): Observable<any> {
    return this.machineDataApi
      .postCNHCompany(action.companyUuid, action.company)
      .pipe(
        tap(() => {
          ctx.setState(
            produce((state) => {
              state.account.selected_company_id = action.company.companyId;
              state.account.selected_company_name = action.company.companyName;
            }),
          );
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  @Action(CNH.GetTasks)
  getTasks(
    ctx: StateContext<CNHStateModel>,
    action: CNH.GetTasks,
  ): Observable<any> {
    ctx.patchState({ tasksLoading: true });
    return this.machineDataApi
      .getTasks(action.companyUuid, TaskOnlineServices.ONLINE_SERVICE_CNH)
      .pipe(
        tap((response: MachineDataTask[]) => {
          ctx.setState(
            produce((state) => {
              state.tasksLoading = false;
              state.tasks = response;
            }),
          );
        }),
        catchError((err) => {
          ctx.patchState({ tasksLoading: false });
          return throwError(() => err);
        }),
      );
  }

  @Action(CNH.DownloadTask)
  downloadTask(
    ctx: StateContext<CNHStateModel>,
    action: CNH.DownloadTask,
  ): Observable<string> {
    return this.machineDataApi
      .downloadTask(action.companyUuid, action.jobId)
      .pipe(
        tap((response: string) => {
          window.open(response);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }
}

export class CNHStateSelectors {
  static slices = createPropertySelectors<CNHStateModel>(CNHState);

  @Selector([CNHStateSelectors.slices.account])
  static companies(account: CNHAccountDetails): CNHCompany[] | [] {
    return account?.companies || [];
  }

  @Selector([CNHStateSelectors.slices.account])
  static selectedCompany(account: CNHAccountDetails): CNHCompany {
    return {
      companyId: account?.selected_company_id,
      companyName: account?.selected_company_name,
    };
  }

  @Selector([CNHStateSelectors.slices.account])
  static tractors(account: CNHAccountDetails): CNHTractor[] | [] {
    return account?.tractors || [];
  }
}
